import React, { useCallback, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/shared/contexts'
import {
  useAppVersions,
  useToggleAppVersion,
  useUpdateAppVersion,
} from '@/shared/hooks'
import type { AppPlatform, AppVersionUpdateRequest } from '@/shared/api'
import { Card, PageHeader } from '@/shared/ui'
import { AppVersionForm } from './AppVersionForm'
import { AppVersionHistory } from './AppVersionHistory'
import { ForcedUpdateConfirmModal } from './ForcedUpdateConfirmModal'

const TABS: Array<{ value: AppPlatform; label: string }> = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
]

const UNSAVED_CHANGES_CONFIRM =
  'Есть несохранённые изменения. При изменении статуса кампании они будут потеряны. Продолжить?'

type PendingAction =
  | {
      kind: 'save'
      platform: AppPlatform
      request: AppVersionUpdateRequest
      minVersion: string
    }
  | { kind: 'toggle'; platform: AppPlatform; isActive: boolean; minVersion: string }

export const AppVersionsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('appVersions', 'edit')

  const [platform, setPlatform] = useState<AppPlatform>('android')
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [dirtyByPlatform, setDirtyByPlatform] = useState<Record<AppPlatform, boolean>>({
    android: false,
    ios: false,
  })

  const { data, isLoading, error } = useAppVersions()
  const update = useUpdateAppVersion()
  const toggle = useToggleAppVersion()

  const settings = data?.items.find((item) => item.platform === platform)

  const handleDirtyChange = useCallback(
    (dirty: boolean) => {
      setDirtyByPlatform((prev) =>
        prev[platform] === dirty ? prev : { ...prev, [platform]: dirty }
      )
    },
    [platform]
  )

  const handlePlatformChange = (next: AppPlatform) => {
    if (next === platform) return
    if (dirtyByPlatform[platform]) {
      if (!window.confirm(UNSAVED_CHANGES_CONFIRM)) return
      setDirtyByPlatform((prev) => ({ ...prev, [platform]: false }))
    }
    setPlatform(next)
  }

  const handleSave = (request: AppVersionUpdateRequest) => {
    if (!canEdit) return
    if (request.update_type === 'forced' && request.is_active) {
      setPending({
        kind: 'save',
        platform,
        request,
        minVersion: request.min_version,
      })
      return
    }
    void update.mutateAsync({ platform, request }).catch(() => undefined)
  }

  const handleToggle = (isActive: boolean) => {
    if (!canEdit || !settings) return
    if (dirtyByPlatform[platform]) {
      if (!window.confirm(UNSAVED_CHANGES_CONFIRM)) return
    }
    if (isActive && settings.update_type === 'forced') {
      setPending({
        kind: 'toggle',
        platform,
        isActive,
        minVersion: settings.min_version,
      })
      return
    }
    void toggle.mutateAsync({ platform, isActive }).catch(() => undefined)
  }

  const handleConfirm = async () => {
    if (!pending) return
    try {
      if (pending.kind === 'save') {
        await update.mutateAsync({ platform: pending.platform, request: pending.request })
      } else {
        await toggle.mutateAsync({
          platform: pending.platform,
          isActive: pending.isActive,
        })
      }
      setPending(null)
    } catch {
      // Ошибка уже показана тостом в хуке
    }
  }

  return (
    <div className="flex-1">
      <PageHeader
        title="Версии приложения"
        description="Управление обновлениями мобильного приложения «Звено»"
      />

      <div className="space-y-6 p-6">
        <div role="tablist" aria-label="Платформа приложения" className="flex flex-wrap gap-2">
          {TABS.map((item) => (
            <button
              key={item.value}
              type="button"
              role="tab"
              id={`app-version-tab-${item.value}`}
              aria-selected={platform === item.value}
              aria-controls="app-version-panel"
              onClick={() => handlePlatformChange(item.value)}
              className={
                platform === item.value
                  ? 'admin-button admin-button-primary'
                  : 'admin-button admin-button-secondary'
              }
            >
              {item.label}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          id="app-version-panel"
          aria-labelledby={`app-version-tab-${platform}`}
        >
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            </div>
          )}

          {!isLoading && error && (
            <Card>
              <p className="text-sm text-red-600">
                {error.message || 'Не удалось загрузить настройки обновления'}
              </p>
            </Card>
          )}

          {!isLoading && !error && !settings && (
            <Card>
              <p className="text-sm text-gray-500">
                Настройки для выбранной платформы не найдены.
              </p>
            </Card>
          )}

          {!isLoading && !error && settings && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="xl:col-span-2">
                <AppVersionForm
                  key={platform}
                  platform={platform}
                  settings={settings}
                  onSave={handleSave}
                  onToggle={handleToggle}
                  onDirtyChange={handleDirtyChange}
                  saving={update.isPending}
                  toggling={toggle.isPending}
                />
              </div>

              <AppVersionHistory platform={platform} />
            </div>
          )}
        </div>
      </div>

      <ForcedUpdateConfirmModal
        isOpen={pending !== null}
        platform={pending?.platform ?? platform}
        minVersion={pending?.minVersion ?? ''}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
        loading={update.isPending || toggle.isPending}
      />
    </div>
  )
}
